import type {
  SYSDialog,
  SYSFeature,
  SYSFeatures,
  SYSPermissions,
  SYSSessionEntries,
  SYSTableDefIds,
} from ".."
import { ovl } from ".."
import type { SYSPermissionModifiers } from "../state"

import { DialogCurrentState } from "../library/dialog/dialogapi"
import { _FocusState } from "../library/sysbaseelement"
import { ScreenCurrent, ScreenCurrentState } from "../library/screenapi"
import {
  _globals,
  _HasPermission,
  _SetFocus,
  _ShowFile,
  SYSSeqId,
  T,
} from "./globals"
import { SYSFormState } from "../library/forms/formapi"
import { SYSField } from "../library/forms/actions"
import { GetFieldLabel } from "../library/forms/controls/helpers"
import { SetTranslatedLabel } from "../library/forms/helper"
import { sessionStore } from "../offlinestorage"
import { TableGet } from "../library/table/tableapi"

export { SYSSeqId }

export const GetValidFileExtensions = (
  tableDefId: SYSTableDefIds = undefined,
  endpoint: string = undefined
): string | null => {
  const state = ovl.getState()
  const fileUploadRestrictions = state.ovl.app.fileUploadRestrictions
  if (!endpoint && tableDefId) {
    const tb = TableGet(tableDefId)
    endpoint = tb.def.server.endpoint?.toLowerCase()
  }
  if (!endpoint) return null
  const override = fileUploadRestrictions.Overrides[endpoint]
  if (!override || !override._default) return null

  let extensions = override._default.AllowedExtensions.slice() // copy array
  if (override._default.ExtendDefaults) {
    extensions = extensions.concat(
      fileUploadRestrictions.DefaultAllowedExtensions
    )
  }

  return extensions.length > 0 ? extensions.join(",") : null
}

export const LanguageSet = async (langCode: string) => {
  await ovl.getActions().ovl.internal._SetLanguage({
    language: langCode,
    startup: false,
  })

  // fixup form labels. they are cached and set by actions
  let formsState = ovl.getState().ovl.forms
  Object.keys(formsState).forEach((formType) => {
    Object.keys(formsState[formType]).forEach((formId) => {
      let formState: SYSFormState = formsState[formType][formId]
      // now go through every current label and replace it
      Object.keys(formState.fields).forEach((fieldKey) => {
        let field: SYSField = formState.fields[fieldKey]
        if (field.ui.labelCaption) {
          // use the same sequence as the labels go through in real usage (in formInit translated, then checking for hooks)
          SetTranslatedLabel(field, field.fieldKey)
          // check for hooks
          GetFieldLabel(field, formState.feature, field.ui.labelCaption)
        }
      })
    })
  })
}

export const PortalTestingModeSet = async (isTest: boolean) => {
  await ovl.getActions().ovl.internal._SetPortalTestingMode(isTest)
}

export const Logout = (force: boolean = false) => {
  ovl.getActions().ovl.user._Logout({ force })
}

export const PermissionHas = (
  permission: SYSPermissions,
  modifier?: SYSPermissionModifiers
): boolean => {
  return _HasPermission(permission, modifier)
}

export type FeatureInfoType = {
  Code: string
  RoleDesc: string
  Val1: number
  Val2: number
  Text1: string
  Text2: string
  Text3: string
  Json1: string
}

// if it returns undefined user is not in role...
export const FeatureInfo = (
  feature: SYSFeatures,
  userId?: number
): FeatureInfoType => {
  let state = ovl.getState()
  if (userId === undefined) {
    userId = state.ovl.user.userCode
  }
  return state.ovl.users?.[userId]?.features?.[feature]
}

export type FeatureGetType<T1 = any /*,T2 = any*/> = {
  FeatureRaw: FeatureInfoType
  TextContains: (val: string, textIndex: number) => boolean
  GetJson1Parsed: () => T1
  /*GetJson2Parsed: (jsonIndex: number) => T2*/
  Found: () => boolean
}

export const FeatureGet = (
  feature: SYSFeatures,
  userId?: number
): FeatureGetType => {
  let state = ovl.getState()
  if (userId === undefined) {
    userId = state.ovl.user.userCode
  }
  let featureState = state.ovl.users?.[userId]?.features?.[feature]
  return {
    FeatureRaw: featureState,
    TextContains: (val: string, textIndex: number = 1) => {
      let text = featureState?.[`Text${textIndex}`]
      if (!text) {
        return false
      }
      return text.indexOf(val) > -1
    },
    GetJson1Parsed: () => {
      let json = featureState?.Json1
      if (!json) {
        return undefined
      }
      return JSON.parse(json)
    },
    Found: () => featureState !== undefined,
  }
}

export const HasFeatureThatStartsWith = (featureStartWith: string): boolean => {
  let state = ovl.getState()
  let userId = state.ovl.user.userCode
  let features = state.ovl.users?.[userId]?.features
  if (!features) {
    return false
  }
  return Object.keys(features).some((s) => s.startsWith(featureStartWith))
}

// export const _FeatureInfoSet = (
//   userId: number,
//   feature: SYSFeatures
// ): FeatureInfoType => {
//   let state = ovl.getState()
//   return state.ovl.users.features?.[feature]
// }

export const FocusGetCurrentId = () => {
  return _globals.lastValidFocusId
}

export const FocusGetPreviousId = () => {
  return _globals.previousValidFocusId
}

export const FocusSet = (
  id: string,
  options?: {
    forceSelect?: boolean
    ignoreIfAlreadySetOnScreenOrDialog?: boolean
    preventScroll?: boolean
  }
): boolean => {
  // focus could be set to any dialog even in the background
  // (this is eg. the case in default ovl when listcontrol pops up but directly closes again because its a good value)
  // so go and check to which dialog this id belongs
  let el = document.getElementById(id)
  if (!el) {
    return false
  }
  let currentDialogs = DialogCurrentState()
  let dialog: SYSDialog
  let dialogInstance: string
  let focusState: _FocusState
  if (currentDialogs.length > 0) {
    while (el.parentElement) {
      if (el.id.startsWith("ovl-dialog-")) {
        let ids = el.id.replace("ovl-dialog-", "").split("-")
        dialog = <SYSDialog>ids[0]
        ids.splice(0, 1)
        dialogInstance = ids.join("-")
        let focusStates = currentDialogs.filter(
          (f) => f.dialog === dialog && f.instance === dialogInstance
        )
        if (focusStates.length > 0) {
          focusState = focusStates[0].focus
        }
        break
      }

      el = el.parentElement
    }
  }

  if (!dialog) {
    focusState = ScreenCurrentState()?.focus
  }
  if (!focusState) {
    return false
  }
  if (options?.ignoreIfAlreadySetOnScreenOrDialog && focusState.focusSet) {
    return false
  }
  if (_SetFocus(id, options?.forceSelect, options?.preventScroll)) {
    focusState.focusSet = true
    return true
  }
}

export const SortGet = (valA: any, valB: any): number => {
  if (valA === valB) {
    return 0
  }
  if (typeof valA === "string" || typeof valB === "string") {
    if (valA === null) {
      valA = ""
    }
    if (valB === null) {
      valB = ""
    }
  }
  if (valA < valB) {
    return -1
  } else {
    return 1
  }
}

export type FileGetOptions = {
  url?: string
  feature?: SYSFeature
  // we can get filename from the content disposition header, but if thats not possible we can also use it as a param
  fileName?: string
  ignoreSnack?: boolean
}

// default url will be api/assets/download
// eg. dataToUrl params for asset are: cat,val, desc
export const FileGet = async (
  dataToUrlParams: {} = undefined,
  options?: FileGetOptions
) => {
  await ovl.getActions().ovl.internal._GetFile({
    url: options?.url,
    dataToUrlParams,
    fileName: options?.fileName,
    feature: options?.feature,
    ignoreSnack: options?.ignoreSnack,
  })
}

export type FileShowOptionsType = {
  feature?: SYSFeature
  mimeType?: string
  fileName?: string
  responseHeaders?: any
}

/*
FileShow
mimeType and fileName will be retrieved from responseHeaders if they are not provided (and responseHeaders are provided)
*/
export const FileShow = async (blob: any, options?: FileShowOptionsType) => {
  await _ShowFile(
    blob,
    options?.mimeType,
    options?.feature,
    options?.fileName,
    options?.responseHeaders
  )
}

export const FileGetDefaultUrl = (actionMethodSuffix: string = "") =>
  `${ovl.getState().ovl.apiUrl}assets/download${actionMethodSuffix}`

export const PopupShow = (popupId: string) => {
  if (ovl.getState().ovl.uiState.currentPopupId !== popupId) {
    ovl.getState().ovl.uiState.currentPopupId = popupId
  } else {
    PopupClose()
  }
}

export const PopupClose = () => {
  ovl.getState().ovl.uiState.currentPopupId = ""
}

export const PopupCurrent = (): string => {
  return ovl.getState().ovl.uiState.currentPopupId
}

export const SessionUpdate = async (
  key: SYSSessionEntries | "TreatAsObjectWithProps",
  val: string | number | { [key in SYSSessionEntries]: any }
) => {
  let currentObj = await sessionStore.get("base")
  if (currentObj === undefined) {
    currentObj = {}
  }
  if (key === "TreatAsObjectWithProps") {
    Object.keys(val).forEach((key) => {
      currentObj[key] = val
      ovl.getState().ovl.session[key] = val
    })
  } else {
    currentObj[key] = val
    ovl.getState().ovl.session[key] = val
  }
  await sessionStore.set("base", currentObj)
}
